/**
 * Involuntary-removal gate for the FullSwing partner auth flow (SMG-4069).
 *
 * Sibling of the explicit-sign-out gate (partnerSignOutGate, FSG-8815), but
 * covering the opposite direction and therefore PERSISTED instead of
 * in-memory:
 *
 *   - FSG-8815 blocks a re-auth INSIDE one page load (post-logout remount) —
 *     an in-memory latch suffices because a fresh document load implies a
 *     fresh QR scan... except it doesn't. Production (SMG-4069) shows the
 *     player's device re-loading `/partner-auth?id_token=...` every ~5
 *     minutes for hours — FullSwing's host software re-navigating, not the
 *     player scanning. Each load re-ran the silent auto-login, whose
 *     server-side JoinSession side effect RE-SEATED a player the simulator
 *     had just removed (and re-enrolled them in engine autoplay): a player
 *     who watched themselves get logged out at the bay was invisibly signed
 *     back in with live wagering, on a loop.
 *
 *   - This gate persists the session_id the player was involuntarily
 *     removed from (observed as a seated→unseated transition on the live
 *     player stream). While it matches the deep link's session_id,
 *     PartnerAuth must NOT auto-login; it asks first ("Rejoin?"). A tap is
 *     the intent signal a background reload can never produce.
 *
 * Lifecycle:
 *   - SET on any stream-observed unseat (Main.tsx projection effect).
 *     Deliberate departures also pass through here first, so every
 *     deliberate path clears it immediately after:
 *   - CLEARED by logout() (explicit sign-out teardown), by the checkout
 *     ("Leave session") flow, by the player accepting the Rejoin prompt,
 *     and by any successful partner login (a fresh scan of a DIFFERENT
 *     session must also drop a stale marker).
 *
 * Failure posture: storage errors fail OPEN (gate reads as unset). The gate
 * is a courtesy guard on our own auto-login; it must never be able to lock
 * a player out of signing in.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import {FULL_SWING_SESSION_ID_STORAGE_KEY} from '../services/fullSwingAuthSessionStorage';

export const PARTNER_REMOVAL_GATE_STORAGE_KEY = 'partner_removed_session_id';

/**
 * Record that the player was unseated from the partner session they are
 * currently authenticated against. Reads the persisted FullSwing
 * session_id; a missing session_id (never partner-authenticated on this
 * device) is a no-op.
 */
export async function markRemovedFromCurrentPartnerSession(): Promise<void> {
  try {
    const sessionId = await AsyncStorage.getItem(FULL_SWING_SESSION_ID_STORAGE_KEY);
    if (!sessionId) return;
    await AsyncStorage.setItem(PARTNER_REMOVAL_GATE_STORAGE_KEY, sessionId);
  } catch {
    console.warn('[partnerRemovalGate] mark failed; auto-rejoin stays ungated');
  }
}

/**
 * Reports whether the auto-login for sessionId is gated by a recorded
 * removal. Does NOT clear the gate — a background reload observing the gate
 * must leave it armed for the next reload; only deliberate actions clear it.
 */
export async function isRemovalGatedForSession(sessionId: string): Promise<boolean> {
  if (!sessionId) return false;
  try {
    return (await AsyncStorage.getItem(PARTNER_REMOVAL_GATE_STORAGE_KEY)) === sessionId;
  } catch {
    return false;
  }
}

/** Drop the marker (deliberate rejoin, logout, checkout, fresh login). */
export async function clearPartnerRemovalGate(): Promise<void> {
  try {
    await AsyncStorage.removeItem(PARTNER_REMOVAL_GATE_STORAGE_KEY);
  } catch {
    console.warn('[partnerRemovalGate] clear failed');
  }
}
